/**
 * MasterOptionEditor - Iter 9.40+ Slice E
 *
 * Reusable dialog for adding/editing a single preference option across the platform.
 * Captures: image upload, label, description, helper text, swatch color, selection-type
 * (when adding a field), and per-option linked products (Slice E - search-picker against
 * db.master_products, no free text).
 */
import { useState, useRef, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Upload, Loader2, ImageIcon, Search, Plus, X } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("session_token")}` });

const SELECTION_TYPES = [
  { value: "multi_select",   label: "Multi-select (multiple options)", note: "Most preference fields use this." },
  { value: "single_select",  label: "Single select (one only)",        note: "Use for skin-type, hair-type." },
  { value: "image_grid",     label: "Image grid (visual picker)",       note: "Use when each option has an image." },
  { value: "sectioned",      label: "Sectioned multi (grouped)",        note: "Use for Accessibility (Physical + Invisible)." },
  { value: "text",           label: "Text / notes",                     note: "Free-form text input." },
];

export function MasterOptionEditor({
  open, onClose, onSave,
  initial = {},
  showSelectionType = false,   // true when creating a NEW field (selection type editable); false for option-only
  title = "Edit option",
  testid = "option-editor",
}) {
  const [draft, setDraft] = useState(initial);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { setDraft(initial); }, [initial, open]);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", "preferences");
      const r = await fetch(`${API}/upload/cloudinary`, { method: "POST", headers: auth(), body: fd });
      if (!r.ok) throw new Error("upload failed");
      const data = await r.json();
      const url = data.secure_url || data.url;
      setDraft(d => ({ ...d, image_url: url }));
      toast.success("Image uploaded");
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    if (!draft.label) { toast.error("Label is required"); return; }
    setSaving(true);
    try {
      await onSave(draft);
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid={testid}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {/* Image upload - first, since the user's biggest complaint was missing per-option images */}
          <div>
            <Label className="text-xs text-stone-500 flex items-center gap-1">
              <ImageIcon className="w-3.5 h-3.5" />Image (upload only - no URL)
            </Label>
            <div className="mt-1.5 flex items-center gap-3">
              <div className="w-20 h-20 rounded-xl border-2 border-dashed border-stone-200 bg-stone-50 flex items-center justify-center overflow-hidden shrink-0">
                {draft.image_url ? (
                  <img src={draft.image_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-6 h-6 text-stone-300" />
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleUpload(e.target.files?.[0])} data-testid={`${testid}-file`} />
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid={`${testid}-upload`}>
                {uploading ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                {draft.image_url ? "Replace image" : "Upload image"}
              </Button>
            </div>
          </div>

          <div>
            <Label className="text-xs text-stone-500">Label</Label>
            <Input value={draft.label || ""} onChange={(e) => setDraft(d => ({ ...d, label: e.target.value }))} className="mt-1 h-8 text-sm" data-testid={`${testid}-label`} />
          </div>

          <div>
            <Label className="text-xs text-stone-500">Description (optional)</Label>
            <Textarea rows={2} value={draft.description || ""} onChange={(e) => setDraft(d => ({ ...d, description: e.target.value }))} className="mt-1 text-sm" data-testid={`${testid}-desc`} />
          </div>

          <div>
            <Label className="text-xs text-stone-500">Helper text (optional)</Label>
            <Input value={draft.helper_text || ""} onChange={(e) => setDraft(d => ({ ...d, helper_text: e.target.value }))} placeholder="Small grey text shown under the option" className="mt-1 h-8 text-sm" data-testid={`${testid}-helper`} />
          </div>

          {/* Slice E - per-option linked products picker (search, no free text) */}
          <LinkedProductsPicker
            value={draft.linked_products || []}
            onChange={(v) => setDraft(d => ({ ...d, linked_products: v }))}
            testid={`${testid}-products`}
          />

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-stone-500">Swatch color (optional)</Label>
              <div className="mt-1 flex items-center gap-2">
                {draft.swatch_color ? (
                  <>
                    <input type="color" value={draft.swatch_color} onChange={(e) => setDraft(d => ({ ...d, swatch_color: e.target.value }))} className="w-9 h-9 rounded cursor-pointer border border-stone-200" data-testid={`${testid}-color`} />
                    <Input value={draft.swatch_color} onChange={(e) => setDraft(d => ({ ...d, swatch_color: e.target.value }))} className="h-8 font-mono text-xs flex-1" placeholder="#dbeafe" />
                    <Button type="button" variant="ghost" size="sm" className="h-8 px-2 text-[10px]" onClick={() => setDraft(d => ({ ...d, swatch_color: "" }))} data-testid={`${testid}-color-clear`}>Clear</Button>
                  </>
                ) : (
                  <Button type="button" variant="outline" size="sm" className="h-8" onClick={() => setDraft(d => ({ ...d, swatch_color: "#e7e5e4" }))} data-testid={`${testid}-color-set`}>
                    Set color
                  </Button>
                )}
              </div>
              <p className="text-[10px] text-stone-400 mt-1">Only used when there's no image. Leave empty if the option doesn't need a swatch.</p>
            </div>
            <div>
              <Label className="text-xs text-stone-500">Sort order</Label>
              <Input type="number" value={draft.sort_order ?? 100} onChange={(e) => setDraft(d => ({ ...d, sort_order: parseInt(e.target.value) || 0 }))} className="mt-1 h-8 text-sm" data-testid={`${testid}-sort`} />
            </div>
          </div>

          {showSelectionType && (
            <div>
              <Label className="text-xs text-stone-500">Selection type</Label>
              <select
                value={draft.field_type || "multi_select"}
                onChange={(e) => setDraft(d => ({ ...d, field_type: e.target.value }))}
                className="mt-1 w-full border border-stone-200 rounded-md px-2 py-1.5 text-sm"
                data-testid={`${testid}-field-type`}
              >
                {SELECTION_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
              <p className="text-[10px] text-stone-400 mt-1">
                {SELECTION_TYPES.find(t => t.value === (draft.field_type || "multi_select"))?.note}
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-stone-100 mt-4">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" onClick={submit} disabled={saving || !draft.label} className="bg-blue-700 hover:bg-blue-800 text-white" data-testid={`${testid}-save`}>
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * LinkedProductsPicker - Slice E search-picker.
 * Fetches db.master_products via /api/admin/master-products, filters client-side,
 * and exposes selected product_ids as the value array. No free-text entry allowed.
 */
function LinkedProductsPicker({ value = [], onChange, testid }) {
  const [products, setProducts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    let alive = true;
    fetch(`${API}/admin/master-products?limit=500`, { headers: auth() })
      .then((r) => r.ok ? r.json() : { products: [] })
      .then((d) => { if (alive) { setProducts(d.products || []); setLoaded(true); } })
      .catch(() => setLoaded(true));
    return () => { alive = false; };
  }, []);

  const matches = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];
    return products
      .filter((p) => !value.includes(p.product_id))
      .filter((p) => {
        const hay = [p.name, p.brand, p.shade, p.category].filter(Boolean).join(" ").toLowerCase();
        return hay.includes(s);
      })
      .slice(0, 10);
  }, [q, products, value]);

  const labelFor = (id) => {
    const p = products.find((x) => x.product_id === id);
    if (!p) return id;
    return `${p.brand ? p.brand + " · " : ""}${p.name || id}${p.shade ? ` (${p.shade})` : ""}`;
  };

  return (
    <div data-testid={testid}>
      <Label className="text-xs text-stone-500">Linked products ({value.length})</Label>
      <div className="relative mt-1">
        <Search className="absolute left-2 top-2.5 w-3.5 h-3.5 text-stone-400" />
        <Input
          className="pl-7 h-8 text-sm"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={loaded ? "Search master products by brand, name, shade…" : "Loading master products…"}
          data-testid={`${testid}-search`}
        />
      </div>
      {matches.length > 0 && (
        <div className="border border-stone-200 rounded mt-1 bg-white divide-y divide-stone-100 max-h-48 overflow-y-auto">
          {matches.map((p) => (
            <button
              key={p.product_id}
              type="button"
              onClick={() => { onChange([...value, p.product_id]); setQ(""); }}
              className="w-full text-left px-2 py-1.5 hover:bg-stone-50 flex items-center gap-2"
              data-testid={`${testid}-add-${p.product_id}`}
            >
              {p.image_url && <img src={p.image_url} alt="" className="w-5 h-5 rounded object-contain" />}
              <span className="text-xs flex-1 truncate">
                {p.brand && <span className="text-stone-500">{p.brand} · </span>}
                <span className="font-medium text-stone-800">{p.name}</span>
                {p.shade && <span className="text-stone-400"> · {p.shade}</span>}
              </span>
              <Plus className="w-3 h-3 text-stone-400" />
            </button>
          ))}
        </div>
      )}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {value.map((id) => (
            <span key={id} className="text-[11px] px-2 py-0.5 rounded-full bg-purple-50 text-purple-800 border border-purple-100 flex items-center gap-1" data-testid={`${testid}-tag-${id}`}>
              {labelFor(id)}
              <button type="button" onClick={() => onChange(value.filter((v) => v !== id))} className="text-purple-400 hover:text-red-500"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}
      {value.length === 0 && q === "" && (
        <p className="text-[10px] text-stone-400 mt-1">Optional - link master products that this option pairs with (e.g. "fair" → MAC NC15 foundation).</p>
      )}
    </div>
  );
}
